import {
  createConversationAPI,
  sendMessageAPI,
  getMessagesAPI,
  sendAttachmentAPI,
  toggleTyping,
  setUserLastSeenAt,
  toggleStatus,
  setCustomAttributes,
  deleteCustomAttribute,
  getConversationsListAPI,
  botHandoffAPI,
} from 'widget/api/conversation';

import { ON_CONVERSATION_CREATED } from 'widget/constants/widgetBusEvents';
import { createTemporaryMessage, getNonDeletedMessages } from './helpers';
import { emitter } from 'shared/helpers/mitt';

export const actions = {
  fetchConversations: async ({ commit }) => {
    commit('setFetchingListStatus', true);
    try {
      const { data } = await getConversationsListAPI();
      commit('setConversations', data);
    } catch (error) {
      // Ignore error
    } finally {
      commit('setFetchingListStatus', false);
    }
  },

  setCurrentConversation: ({ commit }, conversationId) => {
    commit('setCurrentConversationId', conversationId);
  },

  createConversation: async ({ commit, dispatch }, params) => {
    const conversationId = params.conversationId || null;
    commit('setConversationUIFlag', { conversationId, isCreating: true });
    try {
      const { data } = await createConversationAPI(params);
      const { messages, id } = data;
      const [message = {}] = messages;
      commit('pushMessageToConversation', { id, message });
      commit('setCurrentConversationId', id);
      dispatch('conversationAttributes/getAttributes', {}, { root: true });
      // Emit event to notify that conversation is created and show the chat screen
      emitter.emit(ON_CONVERSATION_CREATED);
      return data;
    } catch (error) {
      // Ignore error
      return null;
    } finally {
      commit('setConversationUIFlag', { conversationId, isCreating: false });
    }
  },

  sendMessage: async ({ dispatch, state }, params) => {
    const { currentConversationId } = state;
    const { content, replyTo } = params;
    const message = createTemporaryMessage({ content, replyTo });
    dispatch('sendMessageWithData', {
      conversationId: currentConversationId,
      message,
    });
  },

  sendMessageWithData: async (
    { commit, state },
    { conversationId, message }
  ) => {
    const conversationIdToUse = conversationId || state.currentConversationId;
    const { id, content, replyTo, meta = {} } = message;

    commit('pushMessageToConversation', {
      conversationId: conversationIdToUse,
      message,
    });
    commit('updateMessageMeta', {
      conversationId: conversationIdToUse,
      id,
      meta: { ...meta, error: '' },
    });
    // 隐藏转接按钮
    commit('toggleConnectAgentButton', {
      conversationId: conversationIdToUse,
      show: false,
    });
    try {
      const { data } = await sendMessageAPI(
        content,
        replyTo,
        conversationIdToUse
      );
      commit('pushMessageToConversation', {
        conversationId: conversationIdToUse,
        message: { ...data, status: 'sent' },
      });
    } catch (error) {
      commit('pushMessageToConversation', {
        conversationId: conversationIdToUse,
        message: { ...message, status: 'failed' },
      });
      commit('updateMessageMeta', {
        conversationId: conversationIdToUse,
        id,
        meta: { ...meta, error: '' },
      });
    }
  },

  setLastMessageId: async ({ commit, state }) => {
    const { currentConversationId } = state;
    commit('setLastMessageId', { conversationId: currentConversationId });
  },

  sendAttachment: async ({ commit, state }, params) => {
    const conversationId = state.currentConversationId;
    const {
      attachment: { thumbUrl, fileType },
      meta = {},
    } = params;
    const attachment = {
      thumb_url: thumbUrl,
      data_url: thumbUrl,
      file_type: fileType,
      status: 'in_progress',
    };
    const tempMessage = createTemporaryMessage({
      attachments: [attachment],
      replyTo: params.replyTo,
    });
    commit('pushMessageToConversation', {
      conversationId,
      message: tempMessage,
    });
    try {
      const { data } = await sendAttachmentAPI(conversationId, params);
      commit('updateAttachmentMessageStatus', {
        conversationId,
        message: data,
        tempId: tempMessage.id,
      });
      commit('pushMessageToConversation', {
        conversationId,
        message: { ...data, status: 'sent' },
      });
    } catch (error) {
      commit('pushMessageToConversation', {
        conversationId,
        message: { ...tempMessage, status: 'failed' },
      });
      commit('updateMessageMeta', {
        conversationId,
        id: tempMessage.id,
        meta: { ...meta, error: '' },
      });
      // Show error
    }
  },

  fetchOldConversations: async (
    { commit, state },
    { before, conversationId } = {}
  ) => {
    const conversationIdToUse = conversationId || state.currentConversationId;
    if (!conversationIdToUse) return;

    try {
      commit('setConversationUIFlag', {
        conversationId: conversationIdToUse,
        isFetchingList: true,
      });
      const {
        data: { payload, meta },
      } = await getMessagesAPI({
        before,
        conversation_id: conversationIdToUse,
      });
      const { contact_last_seen_at: lastSeen } = meta;
      const formattedMessages = getNonDeletedMessages({ messages: payload });
      commit('setMetaUserLastSeenAt', {
        conversationId: conversationIdToUse,
        lastSeen,
      });
      commit('setMessagesInConversation', {
        conversationId: conversationIdToUse,
        messages: formattedMessages,
      });
      commit('setConversationUIFlag', {
        conversationId: conversationIdToUse,
        isFetchingList: false,
      });
    } catch (error) {
      commit('setConversationUIFlag', {
        conversationId: conversationIdToUse,
        isFetchingList: false,
      });
    }
  },

  syncLatestMessages: async ({ state, commit }) => {
    const { currentConversationId } = state;
    if (!currentConversationId) return;

    try {
      const conversationDetails = state.details[currentConversationId] || {};
      const { lastMessageId } = conversationDetails;
      if (!lastMessageId) return;

      const {
        data: { payload, meta },
      } = await getMessagesAPI({
        after: lastMessageId,
        conversation_id: currentConversationId,
      });

      const { contact_last_seen_at: lastSeen } = meta;
      const formattedMessages = getNonDeletedMessages({ messages: payload });

      if (!formattedMessages.length) return;

      commit('setMetaUserLastSeenAt', {
        conversationId: currentConversationId,
        lastSeen,
      });
      commit('appendMessagesToConversation', {
        conversationId: currentConversationId,
        messages: formattedMessages,
      });
    } catch (error) {
      // IgnoreError
    }
  },

  clearConversations: ({ commit, state }) => {
    const { currentConversationId } = state;
    commit('clearConversation', { conversationId: currentConversationId });
  },

  addOrUpdateMessage: async ({ commit, state }, data) => {
    const { id, content_attributes, conversation_id } = data;
    const { currentConversationId } = state;
    const usedConversationId = conversation_id || currentConversationId;
    if (content_attributes && content_attributes.deleted) {
      commit('deleteMessage', { conversationId: usedConversationId, id });
      return;
    }
    commit('pushMessageToConversation', {
      conversationId: usedConversationId,
      message: data,
    });
    // Update last activity timestamp for the conversation
    if (data.created_at) {
      commit('updateConversationLastActivity', {
        conversationId: usedConversationId,
        lastActivityAt: data.created_at * 1000, // Assuming created_at is in seconds
      });
    }
    // 如果消息是由代理发送的，更新未读消息计数
    if (data.sender_type === 'User') {
      commit('increaseMetaUnreadCount', { conversationId: usedConversationId });
    }
    commit('setLastMessageId', { conversationId: usedConversationId });
  },

  toggleAgentTyping({ commit, state }, data) {
    const { currentConversationId } = state;
    commit('toggleAgentTypingStatus', {
      conversationId: currentConversationId,
      ...data,
    });
  },

  toggleConnectAgent({ commit, state }, data) {
    const { currentConversationId } = state;
    commit('toggleConnectAgentButton', {
      conversationId: currentConversationId,
      ...data,
    });
  },

  toggleUserTyping: async ({ state }, data) => {
    try {
      const { currentConversationId } = state;
      await toggleTyping({ ...data, conversationId: currentConversationId });
    } catch (error) {
      // IgnoreError
    }
  },

  setUserLastSeen: async ({ commit, getters, state }) => {
    const { currentConversationId = state.currentConversationId } = state;
    if (!currentConversationId || !getters.getMessageCount) {
      return;
    }

    const lastSeen = Date.now() / 1000;
    try {
      commit('setMetaUserLastSeenAt', {
        conversationId: currentConversationId,
        lastSeen,
      });
      await setUserLastSeenAt({
        lastSeen,
        conversation_id: currentConversationId,
      });
    } catch (error) {
      // IgnoreError
    }
  },

  resolveConversation: async ({ state }) => {
    const { currentConversationId } = state;
    await toggleStatus(currentConversationId);
  },

  setCustomAttributes: async (
    { state },
    customAttributes = {},
    conversationId = undefined
  ) => {
    try {
      const { currentConversationId } = state;
      const usedConversationId = conversationId || currentConversationId;
      await setCustomAttributes({
        ...customAttributes,
        conversation_id: usedConversationId,
      });
    } catch (error) {
      // IgnoreError
    }
  },

  deleteCustomAttribute: async (
    { state },
    customAttribute,
    conversationId = undefined
  ) => {
    try {
      const { currentConversationId } = state;
      const usedConversationId = conversationId || currentConversationId;
      await deleteCustomAttribute({
        customAttribute,
        conversation_id: usedConversationId,
      });
    } catch (error) {
      // IgnoreError
    }
  },

  requestBotHandoff: async ({ state }) => {
    const { currentConversationId } = state;
    if (!currentConversationId) {
      throw new Error('No current conversation to handoff.');
    }
    await botHandoffAPI(currentConversationId);
  },
};
